// ============================================================
// 크로스 서브도메인 인증 유틸리티
// - 로그인 시 localStorage + .genomic.cc 쿠키 동시 저장
// - 다른 서브도메인 접속 시 쿠키 → localStorage 자동 동기화
// ============================================================

const COOKIE_MAX_AGE = 86400 * 7; // 7일

// 역할별 localStorage 키 ↔ 쿠키 이름 매핑
export const TOKEN_CONFIG = {
  admin:   { localKey: 'admin_token',              cookieName: 'gc_admin_token' },
  partner: { localKey: 'partner_token',            cookieName: 'gc_partner_token' },
  teacher: { localKey: 'my_role_home_teacher_token', cookieName: 'gc_teacher_token' },
  parent:  { localKey: 'parent_token',             cookieName: 'gc_parent_token' },
  staff:   { localKey: 'staff_token',              cookieName: 'gc_staff_token' },
} as const;

export type AuthRole = keyof typeof TOKEN_CONFIG;

// ── 쿠키 헬퍼 ──────────────────────────────────────────────
function setCookie(name: string, value: string, maxAge: number) {
  const isGenomicDomain = window.location.hostname.includes('genomic.cc');
  const domain = isGenomicDomain ? '; domain=.genomic.cc' : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}${domain}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${encodeURIComponent(name)}=([^;]*)|(?:^|; )${name}=([^;]*)`));
  const val = match?.[1] ?? match?.[2];
  return val ? decodeURIComponent(val) : null;
}

// ── 토큰 저장 (로그인 시 호출) ──────────────────────────────
// localStorage + .genomic.cc 쿠키에 동시 저장
export function storeToken(role: AuthRole, token: string) {
  const { localKey, cookieName } = TOKEN_CONFIG[role];
  localStorage.setItem(localKey, token);
  setCookie(cookieName, token, COOKIE_MAX_AGE);
}

// ── 쿠키 → localStorage 동기화 (앱 초기화 시 호출) ──────────
// 다른 서브도메인에서 넘어온 경우 쿠키의 토큰을 localStorage에 복사
export function syncFromCookie() {
  (Object.keys(TOKEN_CONFIG) as AuthRole[]).forEach(role => {
    const { localKey, cookieName } = TOKEN_CONFIG[role];
    // localStorage에 없을 때만 쿠키에서 복원 (기존 값 보호)
    if (!localStorage.getItem(localKey)) {
      const cookieVal = getCookie(cookieName);
      if (cookieVal) {
        localStorage.setItem(localKey, cookieVal);
      }
    }
  });
}

// ── 현재 로그인된 토큰 조회 ────────────────────────────────
export function getActiveToken(): { token: string; role: AuthRole } | null {
  for (const role of Object.keys(TOKEN_CONFIG) as AuthRole[]) {
    const { localKey } = TOKEN_CONFIG[role];
    const token = localStorage.getItem(localKey);
    if (token) return { token, role };
  }
  return null;
}

// ── 전체 토큰 삭제 (로그아웃) ─────────────────────────────
export function clearAllTokens() {
  (Object.keys(TOKEN_CONFIG) as AuthRole[]).forEach(role => {
    const { localKey, cookieName } = TOKEN_CONFIG[role];
    localStorage.removeItem(localKey);
    setCookie(cookieName, '', 0); // 만료
  });
  // 선생님 부가 정보도 함께 삭제
  ['my_role_home_teacher_center_id', 'my_role_home_teacher_center_name',
   'my_role_home_teacher_class_name', 'my_role_home_teacher_name'].forEach(k => {
    localStorage.removeItem(k);
  });
  // 학부모 부가 정보
  ['parent_email', 'parent_name'].forEach(k => localStorage.removeItem(k));
}

// ── 서비스 간 이동 (토큰 자동 포함) ─────────────────────────
// 기존 URL param 방식 (쿠키가 안될 때 폴백)
export function navigateToDomain(targetUrl: string) {
  const auth = getActiveToken();
  if (!auth) {
    window.location.href = targetUrl;
    return;
  }
  try {
    const url = new URL(targetUrl);
    url.searchParams.set('__gc_token', auth.token);
    url.searchParams.set('__gc_role', auth.role);
    window.location.href = url.toString();
  } catch {
    window.location.href = targetUrl;
  }
}

// ── URL 파라미터 토큰 수신 (폴백용) ──────────────────────────
// ?__gc_token=xxx&__gc_role=partner → localStorage 저장 후 URL 정리
export function consumeUrlToken() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('__gc_token');
  const role = params.get('__gc_role') as AuthRole | null;

  if (!token || !role || !TOKEN_CONFIG[role]) return;

  storeToken(role, token);

  // URL에서 토큰 제거 (보안 - 브라우저 히스토리에 남지 않도록)
  params.delete('__gc_token');
  params.delete('__gc_role');
  const cleanSearch = params.toString();
  const cleanUrl = window.location.pathname +
    (cleanSearch ? '?' + cleanSearch : '') +
    window.location.hash;
  window.history.replaceState(null, '', cleanUrl);
}
